/**
 * NotificationBell — bell icon + dropdown panel for notifications.
 *
 * Mounts in TopBar/Header. Polls every 15s via useNotifications.
 *
 * Detail decisions baked in:
 *   - Pulse ring animation when unread > 0 (CSS, not JS — performant)
 *   - Unread badge with 9+ truncation
 *   - Filter chips inside panel: All / Unread (only shown when > 5 notifications)
 *   - Category icons + colors (matches ActionHistory pattern for consistency)
 *   - Hover-reveal inline delete (X) button per row
 *   - Click row → if has link, navigate + mark read; else just mark read
 *   - Mark-all-read button (only enabled when unread > 0)
 *   - Empty state: "All caught up ✨" with helpful tone
 *   - Relative time on display, absolute on hover (title attribute)
 *   - "Settings" + "View all" footer links
 *   - Click-outside to close
 *   - Keyboard: Escape closes, Tab navigates rows with visible focus ring
 *   - Aria labels for screen readers
 */
import { appLocale } from "@/lib/currency";
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Bell,
  CheckCheck,
  X,
  Settings,
  ShoppingCart,
  Package,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Info,
  Loader2,
  ArrowUpRight,
  ExternalLink,
  CalendarDays,
} from "lucide-react";
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
} from "@/hooks/useNotifications";
import type { Notification } from "@/services/notificationService";
import { toast } from "sonner";
import NotificationPrefs from "./NotificationPrefs";

// ─── Helpers ───────────────────────────────────────────────────────

function relativeTime(iso: string): string {
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return d.toLocaleDateString(appLocale(), { month: "short", day: "numeric" });
  } catch {
    return "—";
  }
}

function absoluteTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(appLocale(), {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * Icon + color for a notification based on category + type.
 * Category takes priority for icon; type sets the color.
 */
function visualsForNotification(n: Notification): {
  Icon: React.ElementType;
  colorBg: string;
  colorText: string;
} {
  const t = n.type;

  // Category-based icon
  let Icon: React.ElementType = Info;
  if (n.category === "order") Icon = ShoppingCart;
  else if (n.category === "inventory") Icon = Package;
  else if (n.category === "report") Icon = CheckCircle2;
  else if (n.category === "hr") Icon = CalendarDays;
  else if (n.category === "system") {
    if (t === "error") Icon = XCircle;
    else if (t === "warning") Icon = AlertCircle;
    else if (t === "success") Icon = CheckCircle2;
    else Icon = Info;
  }

  // Type-based color
  if (t === "error") {
    return {
      Icon,
      colorBg: "bg-rose-500/10",
      colorText: "text-rose-700 dark:text-rose-400",
    };
  }
  if (t === "warning") {
    return {
      Icon,
      colorBg: "bg-amber-500/10",
      colorText: "text-amber-700 dark:text-amber-400",
    };
  }
  if (t === "success") {
    return {
      Icon,
      colorBg: "bg-emerald-500/10",
      colorText: "text-emerald-700 dark:text-emerald-400",
    };
  }
  return {
    Icon,
    colorBg: "bg-primary/10",
    colorText: "text-primary",
  };
}

// ─── Notification Row ──────────────────────────────────────────────

interface NotificationRowProps {
  notification: Notification;
  onSelect: () => void;
  onDelete: () => void;
  isDeleting: boolean;
}

const NotificationRow: React.FC<NotificationRowProps> = ({
  notification,
  onSelect,
  onDelete,
  isDeleting,
}) => {
  const { Icon, colorBg, colorText } = visualsForNotification(notification);
  const unread = !notification.read;

  return (
    <div
      className={`group relative flex items-start gap-3 px-4 py-3 transition-colors cursor-pointer ${
        unread ? "bg-primary/[0.03]" : "bg-transparent"
      } hover:bg-muted/40 focus-within:bg-muted/40`}
    >
      {/* Unread dot indicator */}
      {unread && (
        <div
          className="absolute left-1.5 top-1/2 -translate-y-1/2 h-1.5 w-1.5 rounded-full bg-primary"
          aria-label="Unread"
        />
      )}

      {/* Click target — covers most of the row */}
      <button
        type="button"
        onClick={onSelect}
        className="flex items-start gap-3 flex-1 min-w-0 text-left focus:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded-md"
        aria-label={`${notification.title}: ${notification.message}`}
      >
        <div className={`rounded-full p-1.5 shrink-0 ${colorBg} ${colorText}`}>
          <Icon className="h-3 w-3" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2">
            <div
              className={`text-sm leading-tight ${unread ? "font-semibold text-foreground" : "font-medium text-muted-foreground"}`}
            >
              {notification.title}
            </div>
            <div
              className="text-[10px] text-muted-foreground tabular-nums shrink-0 mt-0.5"
              title={absoluteTime(notification.createdAt)}
            >
              {relativeTime(notification.createdAt)}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
            {notification.message}
          </p>
          {notification.link && (
            <div className="text-[11px] text-primary mt-1 inline-flex items-center gap-0.5 group-hover:text-primary/80">
              View
              <ArrowUpRight className="h-2.5 w-2.5" />
            </div>
          )}
        </div>
      </button>

      {/* Hover-revealed delete button */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        disabled={isDeleting}
        aria-label="Delete notification"
        className="opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity p-1 rounded-md hover:bg-rose-500/10 text-muted-foreground hover:text-rose-600 disabled:opacity-50 shrink-0 self-start"
      >
        {isDeleting ? (
          <Loader2 className="h-3 w-3 animate-spin" />
        ) : (
          <X className="h-3 w-3" />
        )}
      </button>
    </div>
  );
};

// ─── Filter chips ──────────────────────────────────────────────────

type FilterValue = "all" | "unread";

// ─── Main component ────────────────────────────────────────────────

interface NotificationBellProps {
  /** Optional className for the trigger button */
  className?: string;
  /** Where the panel anchors. "right" (default) for top-right Header, "left" if needed */
  anchor?: "left" | "right";
}

export const NotificationBell: React.FC<NotificationBellProps> = ({
  className = "",
  anchor = "right",
}) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<FilterValue>("all");
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const notificationsQuery = useNotifications({
    limit: 20,
    unreadOnly: filter === "unread",
  });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();
  const deleteNotification = useDeleteNotification();

  const data = notificationsQuery.data;
  const notifications = data?.notifications ?? [];
  const unreadCount = data?.unread ?? 0;
  const totalCount = data?.total ?? 0;

  // Click-outside + Escape to close
  useEffect(() => {
    if (!open) return;
    const handleMouse = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("mousedown", handleMouse);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handleMouse);
      document.removeEventListener("keydown", handleKey);
    };
  }, [open]);

  const handleRowSelect = async (n: Notification) => {
    // Mark read if not already
    if (!n.read) {
      markRead.mutate(n._id);
    }
    // Navigate if there's a link
    if (n.link) {
      setOpen(false);
      navigate(n.link);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNotification.mutateAsync(id);
    } catch {
      toast.error("Could not delete notification");
    }
  };

  const handleMarkAll = async () => {
    if (unreadCount === 0) return;
    try {
      await markAllRead.mutateAsync();
    } catch {
      toast.error("Could not mark all as read");
    }
  };

  const showFilterChips = totalCount > 5 || unreadCount > 0;

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* Bell trigger */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ""}`}
        aria-expanded={open}
        aria-haspopup="true"
        className="relative inline-flex items-center justify-center h-9 w-9 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
      >
        <Bell className="h-4 w-4" />

        {/* Unread badge with pulse ring */}
        {unreadCount > 0 && (
          <>
            <span
              className="absolute top-1.5 right-1.5 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-rose-600 text-white text-[10px] font-bold leading-none ring-2 ring-background"
              aria-hidden="true"
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
            {/* Pulse ring — subtle, calls attention without being annoying */}
            <span
              className="absolute top-1.5 right-1.5 h-4 w-4 rounded-full bg-rose-500/60 animate-ping pointer-events-none"
              aria-hidden="true"
              style={{ animationDuration: "2.5s" }}
            />
          </>
        )}
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          className={`absolute top-full ${anchor === "right" ? "right-0" : "left-0"} mt-2 w-[380px] max-w-[calc(100vw-2rem)] rounded-xl border border-border bg-card shadow-xl z-50 overflow-hidden`}
          role="dialog"
          aria-label="Notifications panel"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/20">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-semibold text-foreground">
                Notifications
              </h3>
              {totalCount > 0 && (
                <span className="text-[11px] text-muted-foreground tabular-nums">
                  {totalCount}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={handleMarkAll}
              disabled={unreadCount === 0 || markAllRead.isPending}
              aria-label="Mark all as read"
              className="inline-flex items-center gap-1 text-[11px] text-primary hover:text-primary/80 disabled:text-muted-foreground/60 disabled:cursor-not-allowed transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded px-1.5 py-0.5"
            >
              {markAllRead.isPending ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <CheckCheck className="h-3 w-3" />
              )}
              Mark all read
            </button>
          </div>

          <NotificationPrefs />

          {/* Filter chips */}
          {showFilterChips && (
            <div className="px-4 py-2 border-b border-border flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setFilter("all")}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary ${
                  filter === "all"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground hover:bg-muted/70"
                }`}
              >
                All
              </button>
              <button
                type="button"
                onClick={() => setFilter("unread")}
                className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary ${
                  filter === "unread"
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-foreground hover:bg-muted/70"
                }`}
              >
                Unread
                {unreadCount > 0 && (
                  <span className="ml-1 tabular-nums">({unreadCount})</span>
                )}
              </button>
            </div>
          )}

          {/* Body */}
          <div className="max-h-[480px] overflow-y-auto">
            {notificationsQuery.isLoading ? (
              <div className="divide-y divide-border">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="px-4 py-3 flex items-start gap-3">
                    <div className="h-6 w-6 rounded-full bg-muted animate-pulse shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 w-3/4 bg-muted rounded animate-pulse" />
                      <div className="h-2.5 w-1/2 bg-muted rounded animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : notificationsQuery.isError ? (
              <div className="px-4 py-10 text-center">
                <AlertCircle className="h-8 w-8 mx-auto text-rose-600 mb-2" />
                <p className="text-xs text-muted-foreground">
                  Couldn't load notifications
                </p>
                <button
                  type="button"
                  onClick={() => notificationsQuery.refetch()}
                  className="mt-2 text-[11px] text-primary hover:text-primary/80"
                >
                  Retry
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <div className="text-3xl mb-2" aria-hidden="true">
                  ✨
                </div>
                <p className="text-sm font-medium text-foreground">
                  {filter === "unread"
                    ? "No unread notifications"
                    : "You're all caught up"}
                </p>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {filter === "unread"
                    ? "Everything's been read."
                    : "We'll let you know when something needs attention."}
                </p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications.map((n) => (
                  <NotificationRow
                    key={n._id}
                    notification={n}
                    onSelect={() => handleRowSelect(n)}
                    onDelete={() => handleDelete(n._id)}
                    isDeleting={
                      deleteNotification.isPending &&
                      deleteNotification.variables === n._id
                    }
                  />
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border px-3 py-2 bg-muted/10 flex items-center justify-between gap-2">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/settings?tab=notifications");
              }}
              className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded px-1 py-0.5"
            >
              <Settings className="h-3 w-3" />
              Settings
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                navigate("/notifications");
              }}
              className="inline-flex items-center gap-0.5 text-[11px] text-primary hover:text-primary/80 transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-primary rounded px-1 py-0.5"
            >
              View all
              <ExternalLink className="h-2.5 w-2.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
